import { InputAdornment, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
// Make sure to update your props interface in @types/Components too!
import { MultiDirectionalNumberInputProps } from '../../@types/Components';
import { EDirection } from '../../@types/Enums';
import { IconDoubleArrowActive } from '../../assets/icons'; // Default fallback
import { numberInputButtonStateColor1, numberInputButtonStateColor2, numberInputButtonStateColor3 } from '../../utils/colors';
import { getNumberInputAdornmentStyles } from '../../utils/cssUtils';
import { handleWheelInput, WheelInputConfig } from '../../utils/handleMouseEvents';
import ImsIcon from './ImsIcon';

const MultiDirectionalNumberInput = (props: MultiDirectionalNumberInputProps): JSX.Element => {
  const { 
    value, 
    min, 
    max, 
    direction, 
    onChange, 
    tooltip = '',
    // New Prop: Allow custom icon, default to the Double Arrow
    icon: CustomIcon = IconDoubleArrowActive 
  } = props;

  const [currentValue, setCurrentValue] = useState<number>(value);
  const [currentDirection, setCurrentDirection] = useState<EDirection>(direction);
  
  const getBackgroundColorForDirection = (dir: EDirection) => {
    switch (dir) {
      case EDirection.LEFT: return numberInputButtonStateColor2;
      case EDirection.RIGHT: return numberInputButtonStateColor3;
      case EDirection.BOTH: 
      default: return numberInputButtonStateColor1;
    }
  };

  const [iconBackgroundColor, setIconBackgroundColor] = useState<string>(getBackgroundColorForDirection(direction));

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    setCurrentDirection(direction);
    setIconBackgroundColor(getBackgroundColorForDirection(direction));
  }, [direction]);

  const wheelConfig: WheelInputConfig = {
    unitType: EDirection.BOTH as any,
    minMetric: min,
    maxMetric: max,
    minInches: min,
    maxInches: max,
  };

  const validateAndSetState = (newValue: number): void => {
    let clampedValue = newValue;
    if (newValue < min) clampedValue = min;
    else if (newValue > max) clampedValue = max;
    setCurrentValue(clampedValue);
    onChange(clampedValue, currentDirection);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue)) setCurrentValue(newValue);
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue)) validateAndSetState(newValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const newValue = parseInt((event.target as HTMLInputElement).value, 10);
    if (event.key === 'Enter' && !isNaN(newValue)) {
      validateAndSetState(newValue);
      (event.target as HTMLInputElement).blur();
    }
  };

  const handleDirectionChange = (): void => {
    let nextDirection = EDirection.BOTH;
    if (currentDirection === EDirection.BOTH) nextDirection = EDirection.LEFT;
    else if (currentDirection === EDirection.LEFT) nextDirection = EDirection.RIGHT;
    else if (currentDirection === EDirection.RIGHT) nextDirection = EDirection.BOTH;

    setCurrentDirection(nextDirection);
    setIconBackgroundColor(getBackgroundColorForDirection(nextDirection));
    onChange(currentValue, nextDirection);
  };

  return (
    <TextField
      fullWidth
      type="number"
      value={currentValue}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
      onKeyUp={handleKeyPress}
      onWheel={(e) => handleWheelInput(e, currentValue.toString(), (v) => {
         const val = parseInt(v, 10);
         if(!isNaN(val)) setCurrentValue(val);
      }, wheelConfig)}
      InputProps={{
        startAdornment: (
          <Tooltip title={tooltip} placement="top" disableHoverListener={!tooltip}>
            <InputAdornment
              position="start"
              onClick={handleDirectionChange}
              sx={{
                ...getNumberInputAdornmentStyles(),
                backgroundColor: `${iconBackgroundColor} !important`,
                color: `#757575 !important`,
                cursor: 'pointer'
              }}
            >
              {/* Use the prop here */}
              <ImsIcon Icon={CustomIcon} />
            </InputAdornment>
          </Tooltip>
        ),
        inputProps: { min, max },
      }}
      sx={{
        '& .MuiOutlinedInput-root': { paddingLeft: 0 },
        '& .MuiInputAdornment-root': {
          backgroundColor: numberInputButtonStateColor1,
          padding: '.6rem',
          borderRadius: '50%',
          margin: '.5rem',
          width: '1rem',
          height: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    />
  );
};

export default MultiDirectionalNumberInput;